import { create } from "zustand";
import { devtools } from "zustand/middleware";

/**
 * Transaction Status
 */
export type TransactionStatus =
  | "pending"
  | "confirming"
  | "confirmed"
  | "failed";

/**
 * Transaction Type
 */
export type TransactionType = "token_transfer" | "swap" | "stake" | "other";

/**
 * Transaction Record
 */
export interface Transaction {
  hash: string;
  type: TransactionType;
  status: TransactionStatus;
  chainId: number;
  from: string;
  to?: string;
  value?: string;
  description: string;
  timestamp: number;
  confirmations?: number;
  error?: string;
}

/**
 * Transaction State Interface
 */
interface TransactionState {
  // Transactions map (hash -> transaction)
  transactions: Map<string, Transaction>;

  // Actions
  addTransaction: (transaction: Omit<Transaction, "timestamp">) => void;
  updateTransaction: (hash: string, updates: Partial<Transaction>) => void;
  removeTransaction: (hash: string) => void;
  clearTransactions: () => void;

  // Getters
  getTransaction: (hash: string) => Transaction | undefined;
  getPendingTransactions: () => Transaction[];
  getRecentTransactions: (limit?: number) => Transaction[];
  getTransactionsByType: (type: TransactionType) => Transaction[];
}

/**
 * Transaction Store
 * Manages transaction state and history
 */
export const useTransactionStore = create<TransactionState>()(
  devtools(
    (set, get) => ({
      transactions: new Map(),

      addTransaction: (transaction) =>
        set((state) => {
          const newTransactions = new Map(state.transactions);
          newTransactions.set(transaction.hash, {
            ...transaction,
            timestamp: Date.now(),
          });
          return { transactions: newTransactions };
        }),

      updateTransaction: (hash, updates) =>
        set((state) => {
          const transaction = state.transactions.get(hash);
          if (!transaction) return state;

          const newTransactions = new Map(state.transactions);
          newTransactions.set(hash, { ...transaction, ...updates });
          return { transactions: newTransactions };
        }),

      removeTransaction: (hash) =>
        set((state) => {
          const newTransactions = new Map(state.transactions);
          newTransactions.delete(hash);
          return { transactions: newTransactions };
        }),

      clearTransactions: () => set({ transactions: new Map() }),

      getTransaction: (hash) => {
        return get().transactions.get(hash);
      },

      getPendingTransactions: () => {
        return Array.from(get().transactions.values()).filter(
          (tx) => tx.status === "pending" || tx.status === "confirming",
        );
      },

      getRecentTransactions: (limit = 10) => {
        return Array.from(get().transactions.values())
          .sort((a, b) => b.timestamp - a.timestamp)
          .slice(0, limit);
      },

      getTransactionsByType: (type) => {
        return Array.from(get().transactions.values()).filter(
          (tx) => tx.type === type,
        );
      },
    }),
    { name: "TransactionStore" },
  ),
);
